import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Stack,
  useCallbackRef,
  useDisclosure,
} from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button, Switch, Text, TextField } from '@main/complykit';
import { useDeleteProgramMutation } from '@main/graphql/mutations/DeleteProgram.admin.generated';
import { useDuplicateProgramMutation } from '@main/graphql/mutations/DuplicateProgram.generated';
import {
  UpdateProgramMutationVariables,
  useUpdateProgramMutation,
} from '@main/graphql/mutations/UpdateProgram.generated';
import { useUpdateProgramsManyMutation } from '@main/graphql/mutations/UpdateProgramsMany.generated';
import {
  api as ProgramsApi,
  useGetProgramsQuery,
} from '@main/graphql/queries/GetPrograms.generated';
import { useStableCallback, useUpdateAllQueryData } from '@main/shared/browser';
import { createSorter, hashArrayIndexesById, toError } from '@main/shared/utils';
import {
  actionHelper,
  createColumnHelper,
  errorToast,
  successToast,
  Table,
  useTableRowReorder,
} from '@main/ui';
import { useMemo, useRef, useState } from 'react';

import { useBackofficeSelector } from '../../hooks/redux-toolkit-hooks';
import { getProgramsList, Program } from './slice';

const tableId = 'programs';

export const ProgramsTable = ({ orgId }: { orgId: string }) => {
  const [program, setProgram] = useState<Program>();
  const [programName, setProgramName] = useState('');
  const [isDeleteControls, setIsDeleteControls] = useState(false);

  const cancelRef = useRef<HTMLButtonElement>(null);

  const {
    isOpen: isDialogOpen,
    onOpen: openDialog,
    onClose: closeDialog,
  } = useDisclosure({
    onClose: () => setProgramName(''),
  });

  const [deleteProgram, { isLoading: isDeletingProgram }] = useDeleteProgramMutation();

  const { refetch: refetchPrograms } = useGetProgramsQuery({
    orgId,
  });

  const programs = useBackofficeSelector((state) => getProgramsList(state, orgId));

  const handleDelete = async () => {
    if (programName !== program?.name) {
      errorToast('Program name did not match');
      return;
    }

    try {
      const { delete_program_by_id } = await deleteProgram({
        programId: program.id,
        organizationId: orgId,
        deleteControls: isDeleteControls,
      }).unwrap();

      successToast(delete_program_by_id?.message);
      refetchPrograms();
      closeDialog();
      setProgram(undefined);
      setIsDeleteControls(false);
    } catch (error) {
      errorToast('Something went wrong. Please try again latter');
      datadogLogs.logger.error('Unable to delete program.', {}, toError(error));
    }
  };

  const deleteProgramHandler = useCallbackRef((selectedProgram: Program) => {
    openDialog();
    setProgram(selectedProgram);
  });

  const columns = useProgramsTableColumns({
    orgId,
    handleDelete: deleteProgramHandler,
  });

  const sorter = useMemo(() => createSorter(), []);
  const updateAllQueryData = useUpdateAllQueryData();
  const [updateProgramsMany] = useUpdateProgramsManyMutation();

  const handleProgramsOrderUpdate = async (updatedPrograms: Program[]) => {
    const undoUpdate = updateAllQueryData(ProgramsApi, 'GetPrograms', (draft) => {
      const programsIndexes = hashArrayIndexesById(draft.programs);
      updatedPrograms.forEach((updatedProgram) => {
        const idx = programsIndexes[updatedProgram.id];
        if (idx !== undefined) {
          const draftProgram = draft.programs[idx];
          if (draftProgram) {
            draftProgram.order = updatedProgram.order;
          }
        }
      });
      draft.programs.sort(sorter);
    });

    try {
      await updateProgramsMany({
        updates: updatedPrograms.map((program) => ({
          where: { id: { _eq: program.id } },
          _set: { order: program.order },
        })),
      }).unwrap();
      await refetchPrograms().unwrap();
    } catch (e) {
      undoUpdate();
      errorToast('Reordering of programs failed');
      datadogLogs.logger.error(
        'Reordering of programs failed',
        { programs: updatedPrograms },
        toError(e),
      );
    }
  };

  const tableRowReorder = useTableRowReorder({
    onRowsOrderUpdated: handleProgramsOrderUpdate,
  });

  return (
    <>
      <Table
        id={tableId}
        data={programs}
        columns={columns}
        itemName={{
          singular: 'program',
          plural: 'programs',
        }}
        {...tableRowReorder}
      />

      {/* https://chakra-ui.com/docs/components/alert-dialog/usage#usage */}
      <AlertDialog
        isOpen={isDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeDialog}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete program
            </AlertDialogHeader>

            <AlertDialogBody>
              <Stack spacing={5}>
                <Text>
                  Are you sure you want to delete this program? You cannot revert this action and
                  all program data will be lost. Type the program name{' '}
                  <Text as="em" color="muted">
                    ({program?.name})
                  </Text>
                  .
                </Text>
                <TextField
                  aria-label="Program name"
                  value={programName}
                  onChange={setProgramName}
                />
                <Switch isSelected={isDeleteControls} onChange={setIsDeleteControls}>
                  Delete linked controls that are not cross-mapped to other programs
                </Switch>
              </Stack>
            </AlertDialogBody>

            <AlertDialogFooter gap={2}>
              <Button ref={cancelRef} variant="soft" onPress={closeDialog}>
                Cancel
              </Button>
              <Button
                tone="critical"
                isPending={isDeletingProgram}
                onPress={() => void handleDelete()}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

type OrganizationsTableColumnsProps = {
  orgId: string;
  handleDelete: (program: Program) => void;
};

function useProgramsTableColumns({ orgId, handleDelete }: OrganizationsTableColumnsProps) {
  const { refetch: refetchPrograms } = useGetProgramsQuery({ orgId });
  const [duplicateProgramMutation] = useDuplicateProgramMutation();
  const [updateProgramMutation] = useUpdateProgramMutation();

  const duplicateProgram = useStableCallback(async (program: Program) => {
    try {
      const name = `${program.name} (copy)`;
      await duplicateProgramMutation({ input: { programId: program.id, name } }).unwrap();
      refetchPrograms();
      successToast(`${program.name} duplicated successfully`);
    } catch (error) {
      datadogLogs.logger.error(
        'Failed to duplicate program',
        { programId: program.id },
        toError(error),
      );
      errorToast(`Failed to duplicate program ${program.name}`);
    }
  });

  const updateProgram = useStableCallback(async (update: UpdateProgramMutationVariables) => {
    try {
      await updateProgramMutation(update).unwrap();
      refetchPrograms();
      successToast('Program updated successfully');
    } catch {
      errorToast('Something went wrong. Please try again latter');
    }
  });

  return useMemo(() => {
    const columnHelper = createColumnHelper<Program>();

    return [
      columnHelper.columns.text({
        id: 'name',
        header: 'Name',
        accessorFn: ({ name }) => name,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        enableHiding: false,
        size: 150,
        edit: {
          onChange: (row, name) => updateProgram({ id: row.original.id, programInput: { name } }),
        },
      }),

      columnHelper.columns.boolean({
        id: 'hidden',
        header: 'Visibility',
        accessorFn: ({ hidden }) => !hidden,
        enableSorting: false,
        enableColumnFilter: true,
        edit: {
          onChange: (row, value) => {
            updateProgram({
              id: row.original.id,
              programInput: { hidden: !value },
            });
          },
        },
        size: 1,
        meta: {
          sizeUnit: 'fr',
        },
      }),

      columnHelper.columns.text({
        id: 'framework',
        header: 'Framework',
        accessorFn: ({ framework, template_framework }) =>
          framework?.name ?? template_framework?.name,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        size: 150,
      }),

      columnHelper.columns.text({
        id: 'description',
        header: 'Description',
        accessorFn: ({ description }) => description || '',
        enableGlobalFilter: true,
        enableColumnFilter: true,
        enableSorting: false,
        edit: {
          onChange: (row, description) =>
            updateProgram({ id: row.original.id, programInput: { description } }),
        },
        size: 250,
      }),

      columnHelper.columns.date({
        id: 'date',
        header: 'Start date',
        accessorFn: ({ created_at }) => created_at,
        size: 160,
      }),

      columnHelper.columns.actions({
        menuActions: ({ row }) => [
          actionHelper.menuActionItem({
            icon: DocumentDuplicateIcon,
            label: 'Duplicate',
            onClick: () => duplicateProgram(row.original),
          }),
          actionHelper.menuActionItem({
            icon: TrashIcon,
            label: 'Delete',
            onClick: () => handleDelete(row.original),
            colorScheme: 'red',
          }),
        ],
      }),
    ];
  }, [duplicateProgram, handleDelete, updateProgram]);
}
